/**
 * WELCOME10 after a native newsletter signup, plus #aslf-capture
 * hash scroll and leftover #aslf-all-reviews redirects.
 *
 * Loaded from layout/theme.liquid (every storefront page). Newsletter
 * forms must submit natively: homepage `#aslf-capture`, footer
 * `.email-signup__form`, and `/pages/subscribe`. Shopify's hCaptcha
 * bootstrap (`script#captcha-bootstrap`) hooks
 * `form[action*='/contact']` customer forms. Do not add a submit
 * listener, preventDefault, or fetch() — that drops the captcha token
 * and Shopify never creates the customer.
 *
 * A successful post redirects to the form's `return_to`
 * (`?aslf_subscribed=1`, and `#aslf-capture` on the homepage capture
 * form). That GET does not set `form.posted_successfully?`. On load,
 * `aslf_subscribed=1` or `customer_posted=true` mounts the same panel
 * as snippets/aslf-welcome10-success.liquid, then history.replaceState
 * strips the marker and keeps the hash. Liquid still renders the panel
 * for `posted_successfully?` and the homepage already-taken branch.
 * Errors re-render on the contact POST and never hit `return_to`.
 *
 * Also scrolls `.page-wrapper` for `#aslf-capture`. Leftover
 * `#aslf-all-reviews` hashes redirect to `/pages/reviews`.
 */
(() => {
  const CODE = 'WELCOME10';
  const TITLE = "You're in.";
  const HINT = 'Use code WELCOME10 at checkout for 10% off your first order.';
  const SIGNUP_PARAM = 'aslf_subscribed';

  /**
   * @param {MouseEvent} event
   */
  const onCopyClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-aslf-welcome10-copy]') : null;

    if (!(target instanceof HTMLButtonElement)) return;

    event.preventDefault();

    try {
      await navigator.clipboard.writeText(CODE);
      target.textContent = 'Copied';
      target.setAttribute('aria-label', `Copied code ${CODE}`);
    } catch (error) {
      console.error('ASLF copy code error:', error);
    }
  };

  document.addEventListener('click', onCopyClick);

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  const isNewsletterForm = (form) => {
    if (form.closest('[data-stoq], .stoq, .restock-rocket, restock-rocket-widget')) return false;

    const isCapture = Boolean(form.closest('#aslf-capture'));
    const isEmailSignup = form.classList.contains('email-signup__form');

    if (!isCapture && !isEmailSignup) return false;

    const formType = form.querySelector('input[name="form_type"]');

    if (formType instanceof HTMLInputElement && formType.value !== 'customer') return false;

    if (!form.querySelector('input[name="contact[email]"]')) return false;

    if (form.querySelector('textarea[name="contact[body]"], textarea[name="contact[message]"]')) return false;

    return true;
  };

  /**
   * @returns {HTMLFormElement[]}
   */
  const getNewsletterForms = () => {
    const forms = [];

    for (const form of document.querySelectorAll('#aslf-capture form, form.email-signup__form')) {
      if (form instanceof HTMLFormElement && isNewsletterForm(form)) forms.push(form);
    }

    return forms;
  };

  /**
   * Errors re-render without return_to. If one is on the form, leave it.
   *
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  const formHasBlockingError = (form) => {
    const error = form.querySelector('.aslf-errors, .email-signup__message');

    if (!(error instanceof HTMLElement)) return false;

    if (error.hidden) return false;

    return error.textContent.trim().length > 0;
  };

  /**
   * Capture return_to keeps #aslf-capture. Subscribe and footer do not.
   *
   * @param {HTMLFormElement[]} forms
   * @returns {HTMLFormElement[]}
   */
  const pickSignupTargets = (forms) => {
    const captureTargets = [];
    const otherTargets = [];

    for (const form of forms) {
      if (form.closest('#aslf-capture')) {
        captureTargets.push(form);
      } else {
        otherTargets.push(form);
      }
    }

    if (window.location.hash === '#aslf-capture') return captureTargets;

    if (otherTargets.length > 0) return otherTargets;

    return forms;
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {'capture' | 'compact' | 'prominent'}
   */
  const getPanelVariant = (form) => {
    if (form.closest('#aslf-capture')) return 'capture';

    if (form.closest('.shopify-section-group-footer-group') || form.closest('footer')) return 'compact';

    return 'prominent';
  };

  /**
   * Same nodes and classes as snippets/aslf-welcome10-success.liquid.
   *
   * @param {'capture' | 'compact' | 'prominent'} variant
   * @returns {HTMLElement}
   */
  const createSuccessPanel = (variant) => {
    const panel = document.createElement('div');
    panel.className = `aslf-welcome10 aslf-welcome10--${variant}`;
    panel.setAttribute('role', 'status');
    panel.setAttribute('data-aslf-welcome10', '');

    const title = document.createElement('p');
    title.className = 'aslf-welcome10__title';
    title.tabIndex = -1;
    title.textContent = TITLE;

    const row = document.createElement('div');
    row.className = 'aslf-welcome10__code-row';

    const code = document.createElement('p');
    code.className = 'aslf-welcome10__code';
    code.textContent = CODE;

    const copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'aslf-welcome10__copy';
    copy.setAttribute('data-aslf-welcome10-copy', '');
    copy.setAttribute('aria-label', `Copy code ${CODE}`);
    copy.textContent = 'Copy';

    const hint = document.createElement('p');
    hint.className = 'aslf-welcome10__hint';
    hint.textContent = HINT;

    row.append(code, copy);
    panel.append(title, row, hint);

    return panel;
  };

  /**
   * @param {HTMLFormElement} form
   */
  const showSuccess = (form) => {
    let panel = form.querySelector('[data-aslf-welcome10]');

    if (!(panel instanceof HTMLElement)) {
      panel = createSuccessPanel(getPanelVariant(form));
      form.append(panel);
    }

    panel.hidden = false;

    const fields = form.querySelector('.aslf-capture__fields, .email-signup__input-group');

    if (fields instanceof HTMLElement) fields.hidden = true;

    const title = panel.querySelector('.aslf-welcome10__title');

    if (title instanceof HTMLElement) {
      requestAnimationFrame(() => {
        title.focus({ preventScroll: true });
      });
    }
  };

  /**
   * @returns {boolean}
   */
  const hasSignupMarker = () => {
    const params = new URLSearchParams(window.location.search);

    return params.get(SIGNUP_PARAM) === '1' || params.get('customer_posted') === 'true';
  };

  const stripSignupMarker = () => {
    const params = new URLSearchParams(window.location.search);

    if (!params.has(SIGNUP_PARAM) && !params.has('customer_posted')) return;

    params.delete(SIGNUP_PARAM);
    params.delete('customer_posted');

    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;

    history.replaceState(history.state, '', next);
  };

  /**
   * Same-page hashes that must scroll `.page-wrapper` on desktop.
   * Horizon uses `html/body { overflow: hidden }` ≥990px, so a raw
   * hash does not bring #aslf-capture into view. Reviews browse is
   * `/pages/reviews` — leftover #aslf-all-reviews hashes redirect.
   *
   * Homepage category cards (Hair / Cellular / Skin) navigate to
   * `/pages/hair`, `/pages/cellular`, and `/pages/skincare`. Do not
   * add those paths, or the old section ids `#aslf-system`,
   * `#aslf-science`, and `#aslf-skin`, to HASH_SCROLL_IDS — that
   * preventDefault would scroll the homepage instead of leaving it.
   */
  const HASH_SCROLL_IDS = ['aslf-capture'];
  const LANDING_PAGE_PATHS = ['/pages/hair', '/pages/cellular', '/pages/skincare'];
  const REVIEWS_PAGE_PATH = '/pages/reviews';
  const SQUEEZE_QUERY = window.matchMedia('(min-width: 990px)');

  /**
   * @returns {Element}
   */
  const getPageScroller = () => {
    if (SQUEEZE_QUERY.matches) {
      return document.querySelector('.page-wrapper') ?? document.scrollingElement ?? document.documentElement;
    }

    return document.scrollingElement ?? document.documentElement;
  };

  /**
   * @returns {number}
   */
  const getStickyHeaderOffset = () => {
    const measured = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height'));

    if (Number.isFinite(measured) && measured > 0) return measured;

    const headerGroup = document.querySelector('#header-group');

    return headerGroup instanceof HTMLElement ? headerGroup.offsetHeight : 0;
  };

  /**
   * @param {string} id
   */
  const scrollToHashTarget = (id) => {
    const target = document.getElementById(id);

    if (!target) return;

    const scroller = getPageScroller();
    const offset = getStickyHeaderOffset();
    const targetRect = target.getBoundingClientRect();
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReduced ? 'instant' : 'smooth';
    const usingPageWrapper = scroller instanceof HTMLElement && scroller.classList.contains('page-wrapper');
    const scrollerTop = usingPageWrapper ? scroller.getBoundingClientRect().top : 0;
    const currentScroll = 'scrollTop' in scroller ? scroller.scrollTop : window.scrollY;
    const nextTop = Math.max(0, currentScroll + (targetRect.top - scrollerTop) - offset);

    scroller.scrollTo({ top: nextTop, behavior });
  };

  const revealWelcomeFromRedirect = () => {
    if (!hasSignupMarker()) return;

    const eligible = [];

    for (const form of getNewsletterForms()) {
      if (!formHasBlockingError(form)) eligible.push(form);
    }

    let revealedCapture = false;

    for (const form of pickSignupTargets(eligible)) {
      showSuccess(form);

      if (form.closest('#aslf-capture')) revealedCapture = true;
    }

    stripSignupMarker();

    if (revealedCapture || window.location.hash === '#aslf-capture') {
      scrollToHashTarget('aslf-capture');
    }
  };

  /**
   * @returns {string|null}
   */
  const getHashScrollId = () => {
    const hash = window.location.hash.replace(/^#/, '');

    return HASH_SCROLL_IDS.includes(hash) ? hash : null;
  };

  const maybeRedirectLegacyReviewsHash = () => {
    const hash = window.location.hash.replace(/^#/, '');

    if (hash !== 'aslf-all-reviews') return;

    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';

    if (currentPath !== '/') return;

    window.location.replace(REVIEWS_PAGE_PATH);
  };

  const maybeScrollToHashTarget = () => {
    const id = getHashScrollId();

    if (!id) return;

    scrollToHashTarget(id);
  };

  /**
   * @param {MouseEvent} event
   */
  const onHashScrollLinkClick = (event) => {
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;

    if (!(anchor instanceof HTMLAnchorElement)) return;

    let url;

    try {
      url = new URL(anchor.href, window.location.href);
    } catch {
      return;
    }

    const id = url.hash.replace(/^#/, '');
    const linkPath = url.pathname.replace(/\/$/, '') || '/';

    if (LANDING_PAGE_PATHS.includes(linkPath)) return;

    if (id === 'aslf-all-reviews' && (linkPath === '/' || linkPath === '')) {
      event.preventDefault();
      window.location.assign(REVIEWS_PAGE_PATH);
      return;
    }

    if (!HASH_SCROLL_IDS.includes(id)) return;
    if (!document.getElementById(id)) return;

    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';

    if (linkPath !== currentPath) return;

    event.preventDefault();

    if (window.location.hash !== `#${id}`) {
      history.pushState(history.state, '', `${window.location.pathname}${window.location.search}#${id}`);
    }

    scrollToHashTarget(id);
  };

  maybeRedirectLegacyReviewsHash();
  revealWelcomeFromRedirect();
  maybeScrollToHashTarget();
  requestAnimationFrame(() => {
    requestAnimationFrame(maybeScrollToHashTarget);
  });
  window.addEventListener('load', maybeScrollToHashTarget);
  window.addEventListener('pageshow', () => {
    revealWelcomeFromRedirect();
    requestAnimationFrame(() => {
      requestAnimationFrame(maybeScrollToHashTarget);
    });
  });
  window.addEventListener('hashchange', maybeScrollToHashTarget);
  document.addEventListener('click', onHashScrollLinkClick);
})();
